import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, input, ViewContainerRef } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RestrictedElementDirective } from '@auth';
import { APPLICATION_TYPE, FeatureFlagPipe } from '@core';
import { type Workflow, WorkflowStatus } from '@devices/models';
import { NoDataPlaceholderComponent, NotificationsService } from '@ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { ScheduleInfoComponent } from '../../schedule-info';
import { openCreateWorkflowDialog } from '../create-workflow-dialog';
import { WorkflowAttemptTimeoutComponent } from '../workflow-attempt-timeout';
import { WorkflowStatusComponent } from '../workflow-status/workflow-status.component';
import { WorkflowsStore } from './workflows.store';

@Component({
  imports: [MatIcon, NoDataPlaceholderComponent, MatTooltip, RestrictedElementDirective, NgxSkeletonLoaderModule, WorkflowStatusComponent, MatCardModule, WorkflowAttemptTimeoutComponent, NgClass, ScheduleInfoComponent, FeatureFlagPipe, ScheduleInfoComponent],
  selector: 'avs-workflows',
  templateUrl: './workflows.component.html',
})
export class WorkflowsComponent {
  deviceId = input.required<string>();
  editable = input(true);

  protected isMobile = inject(APPLICATION_TYPE) === 'mobile';
  protected readonly eWorkflowStatus = WorkflowStatus;
  protected readonly workflowStore = inject(WorkflowsStore);

  private readonly vcr = inject(ViewContainerRef);
  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationsService);

  protected openCreateWorkflowDialog() {
    this.handleWorkflowDialog({ deviceId: this.deviceId() });
  }

  protected editWorkflow(workflow: Workflow) {
    this.handleWorkflowDialog({ deviceId: this.deviceId(), workflow });
  }

  protected async changeWorkflowStatus(workflow: Workflow, status: WorkflowStatus) {
    try {
      const updatedWorkflow = await this.workflowStore.updateWorkflow(workflow.id, { status });

      if (updatedWorkflow) {
        this.notificationService.showSuccessMessage(
          `Workflow [${workflow.name}] was successfully updated`,
        );
      }
    } catch (e) {
      this.notificationService.showErrorMessage((e as HttpErrorResponse).message);
    }
  }

  protected canResetWorkflow(workflow: Workflow) {
    return workflow.status === WorkflowStatus.ATTEMPTS_EXCEEDED && !this.workflowStore.isUpdatingOrCreating();
  }

  protected async resetWorkflow(workflow: Workflow) {
    try {
      const updatedWorkflow = await this.workflowStore.resetWorkflow(workflow.id);

      if (updatedWorkflow) {
        this.notificationService.showSuccessMessage(
          `Workflow [${workflow.name}] attempts were reset`,
        );
      }
    } catch (e) {
      this.notificationService.showErrorMessage(this.getBackendErrorMessage(e as HttpErrorResponse));
    }
  }

  private getBackendErrorMessage(error: HttpErrorResponse) {
    const message = error.error?.message;

    return (Array.isArray(message) ? message.join(', ') : message) || error.message;
  }

  private handleWorkflowDialog(data: { deviceId: string; workflow?: Workflow; }) {
    openCreateWorkflowDialog(this.dialog, data, this.vcr);
  }
}
